import React, {useDebugValue} from 'react';

import classNames from 'classnames';

import {useSearchFormContext} from '../../../hooks';

import {
    undef,
    hasValidDestinationValues,
    showDestinationInputFieldError
} from '../../../utils';

import {SearchIcon} from '../../SearchIcon';

const classNamespace = 'search-form';
const fieldClassName = `${classNamespace}__field`;
const inputFieldClassName = `${classNamespace}__input-field`;
const inputFieldErrorClassName = `${inputFieldClassName}--error`;
const destinationInputFieldClassName = `${classNamespace}__destination-input-field`;

// eslint-disable-next-line complexity
function useComponent() {
    const {
        system,
        system: {
            options: {
                site,
                baseUrl,
                appPrefix,
                locale
            }
        },
        media,
        media: {
            settings: {
                isMobile
            }
        },
        abacus,
        form,
        form: {
            options: {
                autoProgress,
                showIcons
            }
        },
        destination,
        destination: {
            actions: {
                setIsTypeaheadInputFocus,
                setAllValues
            },
            events: {
                onBlur,
                onFocus,
                onMount,
                onSearchSelected
            },
            options: {
                filterType,
                googlePlaceApiKey,
                googlePlaceApiUrl,
                hideSelectedFilter,
                highlightMode,
                iconName,
                id,
                matchMode,
                minLength,
                popoverDesktopMaxWidth,
                popoverDesktopMinMarginRight,
                popoverDesktopMinWidth,
                popoverDesktopWidth,
                popoverMaxDisplayCount,
                popoverMaxDisplayCountMobile,
                recentSearchesDisplayMax,
                remotePath,
                retainUserInput,
                showIcon,
                showInlineSuggestion,
                showRecentSearches,
                showResultIcons,
                showResultLabels,
                showSpinner,
                showTrendingSuggestions,
                truncateResultTerms,
                usePopoverAnimation,
                useResultsAnimation
            },
            settings: {
                isTypeaheadInputFocus
            },
            translations: {
                clearSearchLabel,
                noResultsFoundMessage,
                placeholderMessage,
                recentSearchesMessage,
                recentlyViewedLabel,
                resultsFoundMessage,
                selectedMessage,
                trendingDestinationsMessage,
                typeAirportLabel,
                typeCityLabel,
                typeLandmarkLabel,
                typeMetroStationLabel,
                typeNeighborhoodLabel,
                typePropertyLabel,
                typeRegionLabel,
                typeTrainStationLabel,
                whereLabel
            },
            latestValues: {
                destination: search
            }
        },
        dates: {
            actions: {
                setIsOpen: setDatesIsOpen
            }
        },
        guests: {
            actions: {
                setIsOpen: setGuestsIsOpen
            }
        }
    } = useSearchFormContext();

    const className = classNames(fieldClassName, inputFieldClassName, {
        [inputFieldErrorClassName]: showDestinationInputFieldError(media, destination, abacus, system, form)
    }, destinationInputFieldClassName);

    const icon = (showIcon || showIcons && showIcon === undef) && (
        <SearchIcon iconName={iconName}/>
    ) || null;

    const messages = {
        clearsearch: clearSearchLabel,
        destinationplaceholdermsg: placeholderMessage,
        destinationselected: selectedMessage,
        noresultsfound: noResultsFoundMessage,
        recentlyviewed: recentlyViewedLabel,
        recentsearches: recentSearchesMessage,
        resultsfound: resultsFoundMessage,
        trendingdestinations: trendingDestinationsMessage,
        typeairport: typeAirportLabel,
        typecity: typeCityLabel,
        typelandmark: typeLandmarkLabel,
        typemetrostation: typeMetroStationLabel,
        typeneighborhood: typeNeighborhoodLabel,
        typeproperty: typePropertyLabel,
        typeregion: typeRegionLabel,
        typetrainstation: typeTrainStationLabel,
        where: whereLabel
    };

    /* istanbul ignore next */
    function handleBlur(...args) {
        onBlur(...args);
    }

    function handleFocus(...args) {
        if (autoProgress) {
            setDatesIsOpen(false);
            setGuestsIsOpen(false);
        }

        onFocus(...args);
    }

    function handleMount(...args) {
        onMount(...args);
    }

    function handleSearchSelected(...args) {
        const [
            changedValues
        ] = args;

        setAllValues(changedValues);

        // TODO: Move this function to handleBlur
        setIsTypeaheadInputFocus(false);

        if (autoProgress && !isMobile && hasValidDestinationValues(changedValues)) {
            setTimeout(function delayedDatesOpen() {
                setDatesIsOpen(true);
            }, 300);
        }

        onSearchSelected(...args);
    }

    const result = {
        className,
        appPrefix,
        baseUrl,
        filterType,
        googlePlaceApiKey,
        googlePlaceApiUrl,
        hideSelectedFilter,
        highlightMode,
        icon,
        id,
        isMobile,
        isTypeaheadInputFocus,
        locale,
        matchMode,
        messages,
        minLength,
        onBlur: handleBlur,
        onFocus: handleFocus,
        onMount: handleMount,
        onSearchSelected: handleSearchSelected,
        popoverDesktopMaxWidth,
        popoverDesktopMinMarginRight,
        popoverDesktopMinWidth,
        popoverDesktopWidth,
        popoverMaxDisplayCount,
        popoverMaxDisplayCountMobile,
        recentSearchesDisplayMax,
        remotePath,
        retainUserInput,
        search,
        showInlineSuggestion,
        showRecentSearches,
        showResultIcons,
        showResultLabels,
        showSpinner,
        showTrendingSuggestions,
        site,
        truncateResultTerms,
        usePopoverAnimation,
        useResultsAnimation
    };

    useDebugValue(result);

    return result;
}

export {useComponent};
